import type { DashboardStatsData } from '@/api/dashboard-api';
import { Box, Fade, Grid, Skeleton, Typography } from '@mui/material';
import { FC, useEffect, useState } from 'react';

// Custom hook for counting animation
const useCountAnimation = (
  end: number,
  duration: number = 800,
  delay: number = 0,
  loading: boolean = false
) => {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    if (loading) {
      setStarted(false);
      setCount(0);
      return;
    }

    const startTimer = setTimeout(() => {
      setStarted(true);
    }, delay);

    return () => clearTimeout(startTimer);
  }, [delay, loading]);

  useEffect(() => {
    if (!started || loading) return;

    const startTime = Date.now();
    const timer = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      setCount(Math.floor(end * easeOutQuart));

      if (progress >= 1) {
        clearInterval(timer);
        setCount(end);
      }
    }, 16);

    return () => clearInterval(timer);
  }, [end, duration, started, loading]);

  return count;
};

interface StatusCardProps {
  title: string;
  value: number | string;
  loading?: boolean;
  delay?: number;
  chartsReady?: boolean;
}

const DashboardStatusCard: FC<StatusCardProps> = ({
  title,
  value,
  loading = false,
  delay = 0,
  chartsReady = false,
}) => {
  const numericValue =
    typeof value === 'number' ? value : parseInt(value.toString()) || 0;

  const [showContainer, setShowContainer] = useState(false);

  const animatedValue = useCountAnimation(
    numericValue,
    1000,
    delay + 50,
    loading || !chartsReady
  );

  const hasData = numericValue > 0;

  useEffect(() => {
    const timer = setTimeout(() => setShowContainer(true), Math.max(0, delay));
    return () => clearTimeout(timer);
  }, [delay]);

  return (
    <Fade in={showContainer} timeout={500}>
      <Box
        sx={{
          bgcolor: '#fff',
          borderRadius: 2,
          boxShadow: 2,
          p: 2,
          minHeight: { xs: 120, sm: 140, md: 150 },
          height: '100%',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          position: 'relative',
          zIndex: 2,
          transition: 'all 0.3s ease',
          '&:hover': { transform: 'translateY(-3px) scale(1.02)' },
        }}
      >
        {/* Title - fixed position */}
        <Typography sx={{ fontSize: 13, color: '#111827', mb: 1 }}>
          {title}
        </Typography>

        {/* Value slot - fixed height so nothing shifts */}
        <Box
          sx={{
            minHeight: 36,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {loading ? (
            <Skeleton variant="text" width="45%" height={36} />
          ) : !hasData ? (
            <Typography sx={{ fontSize: 13, color: '#6B7280' }}>
              No data available
            </Typography>
          ) : (
            <Typography sx={{ fontSize: 28, fontWeight: 100, color: '#111827' }}>
              {animatedValue}
            </Typography>
          )}
        </Box>
      </Box>
    </Fade>
  );
};

export const DBStatusCards: FC<{
  chartsReady?: boolean;
  stats?: DashboardStatsData;
  loading?: boolean;
}> = ({ chartsReady = false, stats, loading = false }) => {
  const [structureReady, setStructureReady] = useState(false);

  const cards = [
    { title: 'Pending Quotation', value: stats?.pendingQuotation?.count ?? 0 },
    { title: 'Pending Approval', value: stats?.pendingApproval?.count ?? 0 },
    { title: 'Pending Schedule', value: stats?.pendingSchedule?.count ?? 0 },
    { title: 'Completed', value: stats?.completed?.count ?? 0 },
  ];

  useEffect(() => {
    const timer = setTimeout(() => setStructureReady(true), 300);
    return () => clearTimeout(timer);
  }, []);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        height: '100%',
        width: '100%',
      }}
    >
      <Grid container spacing={2} sx={{ flex: 1 }}>
        {cards.slice(0, 2).map((card, index) => (
          <Grid item xs={12} sm={6} key={card.title} sx={{ display: 'flex' }}>
            <DashboardStatusCard
              title={card.title}
              value={card.value}
              loading={!structureReady || loading || !stats}
              chartsReady={chartsReady}
              delay={index * 150}
            />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2} sx={{ flex: 1 }}>
        {cards.slice(2).map((card, index) => (
          <Grid item xs={12} sm={6} key={card.title} sx={{ display: 'flex' }}>
            <DashboardStatusCard
              title={card.title}
              value={card.value}
              loading={!structureReady || loading || !stats}
              chartsReady={chartsReady}
              delay={(index + 2) * 150}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};
